import React,  { useState, useEffect } from 'react';

/**
 * 사용자의 브라우저 언어 설정을 가져옵니다.
 * @returns {string} 언어 코드 (예: 'ko-KR', 'en-US')
 */
function getUserLocale() {
  // navigator.language가 존재하면 그 값을 사용하고, 없으면 기본값 'en-US'를 반환
  return navigator.language || 'en-US';
}


// 시간 단위 상수도 전역으로 선언합니다.
const DIVISIONS = [
  { amount: 60, name: 'seconds' },
  { amount: 60, name: 'minutes' },
  { amount: 24, name: 'hours' },
  { amount: 7, name: 'days' },
  { amount: 4.34524, name: 'weeks' },
  { amount: 12, name: 'months' },
  { amount: Infinity, name: 'years' },
];

/**
 * [Named Export] 날짜를 "방금 전", "N분 전"과 같은 상대 시간으로 변환합니다.
 * @param {string | number | Date} dateInput - 날짜 문자열, 타임스탬프, 또는 Date 객체
 * @returns {string} 변환된 시간 텍스트
 */
export function formatRelativeTime(dateInput) {
  if (!dateInput) return ''; // 입력값이 없을 경우 빈 문자열 반환

  const rtf = new Intl.RelativeTimeFormat(getUserLocale(), { numeric: 'auto' });

  const now = new Date();
  const date = new Date(dateInput);
  let duration = (date.getTime() - now.getTime()) / 1000; // 초 단위 차이

  if (Math.abs(duration) < 5) {
      return "방금 전";
  }

  for (let i = 0; i < DIVISIONS.length; i++) {
    const division = DIVISIONS[i];
    if (Math.abs(duration) < division.amount) {
      const unit = division.name.slice(0, -1);
      return rtf.format(Math.round(duration), unit);
    }
    duration /= division.amount;
  }
}

/**
 * 날짜를 지능적으로 포맷합니다.
 * 최근 날짜는 "N분 전"으로, 오래된 날짜는 "YYYY.MM.DD"로 표시합니다.
 * @param {string | number | Date} dateInput - 날짜 입력
 * @returns {string} 변환된 시간 텍스트
 */
export function formatSmartTime(dateInput) {
  if (!dateInput) return '';

  const rtf = new Intl.RelativeTimeFormat(getUserLocale(), { numeric: 'auto' });

  const now = new Date();
  const date = new Date(dateInput);
  let duration = (date.getTime() - now.getTime()) / 1000;

  // 1. "방금 전" 처리
  if (Math.abs(duration) < 5) {
      return "방금 전";
  }

  // 2. 7일 이내인 경우 상대 시간으로 표시
  for (let i = 0; i < DIVISIONS.length; i++) {
    const division = DIVISIONS[i];
    if (Math.abs(duration) < division.amount) {
      const unit = division.name.slice(0, -1);
      return rtf.format(Math.round(duration), unit);
    }
    duration /= division.amount;
  }
  
  // 3. 7일 이상 지난 경우 절대 날짜(YYYY.MM.DD)로 표시
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).replace(/\. /g, '.').slice(0, -1); // "YYYY. MM. DD." -> "YYYY.MM.DD"
}


// --- 2. React 컴포넌트: 자동으로 업데이트되는 상대 시간 UI ---

/**
 * [Default Export] 날짜를 상대 시간으로 표시하고 자동으로 업데이트하는 컴포넌트
 * @param {{ date: string | number | Date }} props
 */
function TimeAgo({ date }) {
  const [displayText, setDisplayText] = useState(() => formatSmartTime(date));

  useEffect(() => {
    // 1분마다 시간을 다시 계산하여 UI를 업데이트
    const intervalId = setInterval(() => {
      setDisplayText(formatSmartTime(date));
    }, 60000); // 1분

    // 컴포넌트가 사라질 때 interval 정리
    return () => clearInterval(intervalId);
  }, [date]);

  return (
    <time dateTime={new Date(date).toISOString()}>
      {displayText}
    </time>
  );
}

export default TimeAgo;